<template>
  <div class="payment-method-selection">
    <div class="payment-method-selection__container">
      <h3 class="payment-method-selection__title">
        {{ $t('landingpage.checkout-page.payment.title') }}
      </h3>

      <div class="payment-method-selection__intro">
        <p>{{ $t('landingpage.checkout-page.payment.intro') }}</p>
      </div>

      <div class="payment-method-selection__list">
        <template v-for="method in tileOrder" :key="method">
          <!-- Pay online -->
          <label
            v-if="method === 'online'"
            for="payment-method-online"
            class="payment-method-selection__item"
            :class="{
              'payment-method-selection__item--selected':
                modelValue === 'online',
            }"
          >
            <input
              id="payment-method-online"
              type="radio"
              name="payment-method"
              class="payment-method-selection__input"
              value="online"
              :checked="modelValue === 'online'"
              @change="select('online')"
            />

            <div class="payment-method-selection__content">
              <div class="payment-method-selection__icon">
                <FontAwesomeIcon :icon="['far', 'credit-card']" />
              </div>

              <div class="payment-method-selection__details">
                <p class="payment-method-selection__name">
                  {{ $t('landingpage.checkout-page.payment.online.title') }}
                </p>
                <p class="payment-method-selection__description">
                  {{
                    $t('landingpage.checkout-page.payment.online.description')
                  }}
                </p>
              </div>

              <div
                v-if="onlineBadges.length"
                class="payment-method-selection__brands"
              >
                <span
                  v-for="badge in onlineBadges"
                  :key="badge.key"
                  class="payment-method-selection__brand"
                  :class="`payment-method-selection__brand--${badge.key}`"
                >
                  {{ badge.label }}
                </span>
              </div>

              <div
                v-if="modelValue === 'online'"
                class="payment-method-selection__check"
              >
                <FontAwesomeIcon :icon="['fas', 'check']" />
              </div>
            </div>
          </label>

          <!-- Bank transfer -->
          <label
            v-else-if="method === 'bank_transfer'"
            for="payment-method-bank-transfer"
            class="payment-method-selection__item"
            :class="{
              'payment-method-selection__item--selected':
                modelValue === 'bank_transfer',
            }"
          >
            <input
              id="payment-method-bank-transfer"
              type="radio"
              name="payment-method"
              class="payment-method-selection__input"
              value="bank_transfer"
              :checked="modelValue === 'bank_transfer'"
              @change="select('bank_transfer')"
            />

            <div class="payment-method-selection__content">
              <div class="payment-method-selection__icon">
                <FontAwesomeIcon :icon="['far', 'file-invoice-dollar']" />
              </div>

              <div class="payment-method-selection__details">
                <p class="payment-method-selection__name">
                  {{
                    $t('landingpage.checkout-page.payment.bank-transfer.title')
                  }}
                  <span class="payment-method-selection__subtitle">
                    {{
                      $t(
                        'landingpage.checkout-page.payment.bank-transfer.pay-by-invoice',
                      )
                    }}
                  </span>
                </p>
                <p class="payment-method-selection__description">
                  {{
                    $t(
                      'landingpage.checkout-page.payment.bank-transfer.description',
                    )
                  }}
                  <template v-if="formattedDueDate">
                    {{
                      $t(
                        'landingpage.checkout-page.payment.bank-transfer.payable-by',
                        { date: formattedDueDate },
                      )
                    }}
                  </template>
                </p>
              </div>

              <div
                v-if="modelValue === 'bank_transfer'"
                class="payment-method-selection__check"
              >
                <FontAwesomeIcon :icon="['fas', 'check']" />
              </div>
            </div>
          </label>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { formatDate } from '@/composables/date'

const props = defineProps({
  modelValue: { type: String, default: () => null },
  onlinePaymentMethods: { type: Array, default: () => [] },
  bankTransferDueDate: { type: String, default: () => null },
  bankTransferPreferred: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

// Brand chips for the "Pay online" tile. Keyed by Stripe payment method type; types we have no
// label for are skipped. Brand names are not localised.
const ONLINE_BADGE_LABELS = {
  card: 'Card',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  link: 'Link',
}

const tileOrder = computed(() =>
  props.bankTransferPreferred
    ? ['bank_transfer', 'online']
    : ['online', 'bank_transfer'],
)

const onlineBadges = computed(() =>
  props.onlinePaymentMethods
    .filter((method) => Object.hasOwn(ONLINE_BADGE_LABELS, method))
    .map((method) => ({ key: method, label: ONLINE_BADGE_LABELS[method] })),
)

const formattedDueDate = computed(() =>
  props.bankTransferDueDate ? formatDate(props.bankTransferDueDate) : null,
)

const select = (method) => emit('update:modelValue', method)
</script>
